<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useDropZone, useFileDialog } from '@vueuse/core';

import { convertFileArrayToFileList } from '@/utils/format';
import AttachmentsCustomTable from '@/components/customTables/AttachmentsCustomTable.vue';
import DropZoneArea from '@/components/dropZones/DropZoneArea.vue';
import DropZoneButton from '@/components/dropZones/DropZoneButton.vue';
import useField from '@/composables/useField';

export interface Props {
  modelValue?: File[];
  disabled?: boolean;
  limit?: number;
  variant?: 'button' | 'dropzone';
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
  limit: 0,
  variant: 'button',
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref<File[]>(props.modelValue);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});

const dropZoneRef = ref<HTMLDivElement>();

const fileInput = ref<HTMLInputElement>();

const isLimitOrMore = computed(() => {
  return !!(props.limit && fieldValue.value.length >= props.limit);
});

function onDrop(fileArray: File[] | null): void {
  if (isLimitOrMore.value) return;
  if (props.disabled) return;
  if (!fileArray) return;
  fileArray.forEach((file) => {
    fieldValue.value.push(file);
  });
  fieldValue.value = [...fieldValue.value];
}

const isMultiple = computed(() => {
  return props.limit - fieldValue.value.length !== 1;
});

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  multiple: isMultiple.value, // TODO make it reactive
  preventDefaultForUnhandled: false,
});

onMounted(() => {
  fileInput.value = document.createElement('input');
});

const { /* files, */ open, /* reset, */ onCancel, onChange } = useFileDialog({
  multiple: isMultiple.value, // TODO make it reactive
  directory: false,
  input: fileInput.value,
});

onChange((fileList: FileList | null) => {
  if (!fileList) return;
  [...fileList].forEach((file) => {
    fieldValue.value.push(file);
  });
  fieldValue.value = [...fieldValue.value];
});

/* eslint-disable-next-line @typescript-eslint/no-empty-function */
onCancel(() => {});

function deleteFiles(index: number): void {
  fieldValue.value.splice(index, 1);
  fieldValue.value = [...fieldValue.value];
}

watch(fieldValue, () => {
  if (!fileInput.value) return;
  // update internal input files in useFileDialog, otherwise the same file after deletion is not adding
  fileInput.value.files = convertFileArrayToFileList(fieldValue.value);
});
</script>

<template>
  <div
    :class="$style.detailedJobAttachmentsDropZoneField"
    data-testid="detailedJobAttachmentsDropZoneField"
  >
    <div v-if="props.limit" class="mt-n1 mb-3">
      Attachments,
      <span :class="$style.limitText">
        {{ props.limit }}
        <template v-if="props.limit !== 1">files</template>
        <template v-else>file</template>
        limit
      </span>
    </div>
    <div ref="dropZoneRef">
      <DropZoneButton
        v-if="props.variant === 'button'"
        :is-over="isOverDropZone"
        :is-disabled="isLimitOrMore || props.disabled"
        :is-multiple="isMultiple"
        @open="open"
      />
      <DropZoneArea
        v-else
        :is-over="isOverDropZone"
        :is-disabled="isLimitOrMore || props.disabled"
        @open="open"
      />
    </div>
    <AttachmentsCustomTable
      v-if="fieldValue.length"
      :is-hide-thead="limit === 1"
      :attachment-files="fieldValue"
      :delete-files="deleteFiles"
      :class="$style.attachmentsTable"
      class="mt-2"
    />
    <div
      v-if="props.limit && fieldValue.length > props.limit"
      class="v-input--error v-text-field"
    >
      <div class="v-input__details">
        <div class="v-messages">
          <div class="v-messages__message">
            The files limit is exceeded, please remove some files from the list
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobAttachmentsDropZoneField {
  color: colors.$color-text-secondary;
  line-height: 1.4;
}
  .limitText {
    color: colors.$color-text-tertiary;
  }
  .attachmentsTable {
    width: 100%;
  }
</style>
